import { JSX } from "react";
import { ComponentTypes } from "../componentTypes";
import type {
  All3PageContent,
  All4PageContent,
  All5PageContent,
  All7PageContent,
  All8PageContent,
  AllPageContent,
  Extended2PageContent,
  MarginContent,
  MarginComponent,
} from "../types";
import type { RendererConfig } from "./rendererConfig";
import { COMPONENT_REGISTRY } from "./componentRegistry";


type RendererComponents =
  | AllPageContent
  | Extended2PageContent
  | All3PageContent
  | All4PageContent
  | All5PageContent
  | All7PageContent
  | All8PageContent;

/** Page renderer component props. */
export interface PageRendererProps {
  components: RendererComponents;
  config?: RendererConfig;
  /**
   * Extra className appended to the FIRST component's wrapper div. Used by
   * &lt;PageShell&gt; to apply layout-driven classes (e.g., mobile bleed) without
   * the renderer needing to know about layout semantics.
   */
  firstWidgetClassName?: string;
  /** Extra className appended to the LAST component's wrapper div. */
  lastWidgetClassName?: string;
  /**
   * Effective page width mode, forwarded by &lt;PageShell&gt;. On a `fluid` page the
   * &lt;main&gt; has no gutter, so blocks that do NOT self-style would run to the
   * viewport edge. The renderer gives those blocks the standard container.
   * Constrained pages are untouched — they already inherit the main's gutter.
   */
  layoutMode?: "fluid" | "constrained";
}

/**
 * Blocks that expect a page gutter instead of drawing their own. On a fluid
 * page they get the canonical container so a page can mix full-bleed bands
 * (heroes, the subject header) with ordinary content without every renderer
 * having to duplicate the container.
 */
const FLUID_CONTAINER_TYPES = new Set<string>([
  ComponentTypes.FxRatesExplorer,
  ComponentTypes.SeeAlso,
]);

const FLUID_CONTAINER_CLASS = "mx-auto w-full max-w-[75rem] px-4 md:px-8";

const DEFAULT_MARGIN_CLASSES: Required<MarginContent> = {
  mobile: "mt-8",
  md: "md:mt-10",
  lg: "lg:mt-12",
};

/** Per-type layout overrides on the wrapper (applied in addition to spacing). */
const LAYOUT_CLASSES_BY_TYPE: Partial<Record<string, string>> = {
  [ComponentTypes.PageHeader]: "w-full col-span-full", // header takes full horizontal space
};

/**
 * Per-type top-spacing overrides. Vertical rhythm between blocks is decided
 * here — in the page renderer, on the wrapper — rather than inside individual
 * renderers, so spacing stays consistent no matter which blocks an editor
 * stacks. `mt-0` entries are full-bleed sections that manage their own
 * spacing (heroes, blue bands, the below-header bar).
 */
const SPACING_CLASSES_BY_TYPE: Partial<Record<string, string>> = {
  [ComponentTypes.ArticleHero]: "mt-0",
  [ComponentTypes.SectionHeading]: "mt-10 md:mt-14 lg:mt-16",
  [ComponentTypes.ActivityGrid]: "mt-6",
  [ComponentTypes.PaginatedActivityGrid]: "mt-6",
  [ComponentTypes.PersonCardGrid]: "mt-6",
  [ComponentTypes.EventGrid]: "mt-6",
  [ComponentTypes.ArticleGrid]: "mt-6",
  [ComponentTypes.LatestArticleGrid]: "mt-6",
  [ComponentTypes.LinkTileGrid]: "mt-8 md:mt-10 lg:mt-12",
  [ComponentTypes.Callout]: "mt-6 md:mt-8 lg:mt-10",
  [ComponentTypes.StatsGrid]: "mt-6",
  [ComponentTypes.SplitImageCallout]: "mt-8 md:mt-10 lg:mt-12",
  [ComponentTypes.BlueCallout]: "mt-8 md:mt-10 lg:mt-12",
  // Meme respiration que LinkTileGrid : ce sont les deux blocs de navigation
  // pleine largeur du site (panneaux de repertoire et tuiles). `mt-6` les
  // collait au paragraphe qui les precede.
  [ComponentTypes.TextCardGrid]: "mt-8 md:mt-10 lg:mt-12",
  [ComponentTypes.LabeledTextGrid]: "mt-6",
  [ComponentTypes.CompanyReportGrid]: "mt-6",
  [ComponentTypes.BlogArticleGrid]: "mt-6",
  [ComponentTypes.TextBlock]: "mt-4",
  [ComponentTypes.RichText]: "mt-4",
  [ComponentTypes.Gallery]: "mt-6",
  [ComponentTypes.BannerImage]: "mt-6",
  [ComponentTypes.FullWidthImage]: "mt-6",
  [ComponentTypes.IconCardGrid]: "mt-6",
  [ComponentTypes.TextWithSideImage]: "mt-6",
  [ComponentTypes.TextWithSidePanel]: "mt-6",
  [ComponentTypes.SingleLink]: "mt-0",
  [ComponentTypes.LegislationList]: "mt-6",
  [ComponentTypes.NumberedList]: "mt-6",
  [ComponentTypes.TwoColumnList]: "mt-6",
  [ComponentTypes.TextWithFigure]: "mt-6 md:mt-8",
  [ComponentTypes.BilletTabs]: "mt-6 md:mt-8",
  [ComponentTypes.BilletSecurity]: "mt-8 md:mt-12",
  [ComponentTypes.ContactForm]: "mt-6",
  [ComponentTypes.SeriesExplorer]: "mt-6",
  [ComponentTypes.KeyMetricsExplorer]: "mt-6",
  [ComponentTypes.FxRatesExplorer]: "mt-6",
  [ComponentTypes.Hero]: "mt-0",
  [ComponentTypes.HeroCarousel]: "mt-0",
  [ComponentTypes.NewsSection]: "mt-0",
  [ComponentTypes.NewsList]: "mt-6",
  [ComponentTypes.CtaBanner]: "mt-6",
  [ComponentTypes.StatsSummary]: "mt-0",
  [ComponentTypes.AudienceTools]: "mt-0",
  [ComponentTypes.SearchBar]: "mt-0",
  [ComponentTypes.PublicationsUpdates]: "mt-0",
  [ComponentTypes.HomeHero]: "",
  [ComponentTypes.HomeNews]: "mt-0",
  [ComponentTypes.HomeBlueStats]: "mt-0",
  [ComponentTypes.HomeBlueTools]: "mt-0",
  [ComponentTypes.HomePubUpdatesSection]: "mt-0",
  [ComponentTypes.NewsletterSignup]: "mt-0",
  [ComponentTypes.AccordionSection]: "mt-6",
  [ComponentTypes.Coordonnees]: "mt-6",
  [ComponentTypes.DynamicComponents]: "mt-6",
  [ComponentTypes.KeyIndicatorsBar]: "mt-0",
  [ComponentTypes.InformationHub]: "mt-0",
  [ComponentTypes.MarketsBoard]: "mt-0",
  [ComponentTypes.DataRecordDetail]: "mt-6",
  [ComponentTypes.VideoEmbed]: "mt-6",
  [ComponentTypes.LayoutSection]: "mt-6 md:mt-8",
  [ComponentTypes.SmartCardGrid]: "mt-6 md:mt-8",
  [ComponentTypes.CtaButton]: "mt-6",
  [ComponentTypes.Quote]: "mt-6 md:mt-8",
  [ComponentTypes.Divider]: "mt-8 md:mt-10",
  [ComponentTypes.SeeAlso]: "mt-10",
};

const buildMarginClassName = (content?: MarginContent | null) => {
  const mobile = content?.mobile?.trim() || DEFAULT_MARGIN_CLASSES.mobile;
  const md = content?.md?.trim() || DEFAULT_MARGIN_CLASSES.md;
  const lg = content?.lg?.trim() || DEFAULT_MARGIN_CLASSES.lg;
  return [mobile, md, lg].filter(Boolean).join(" ").trim();
};

/** Main page renderer: orchestrates sequential display of all components. */
export function PageRenderer({ components, config, firstWidgetClassName, lastWidgetClassName, layoutMode }: PageRendererProps) {
  const lastIndex = components.length - 1;
  // Blocks that adapt to the page width mode (subject header) read it from the
  // config, so the registry signature stays uniform.
  const effectiveConfig: RendererConfig | undefined =
    layoutMode ? { ...(config ?? {}), layoutMode } : config;
  return (
    <div>
      {components.map((component, index) => {
        const prevComponentType = index > 0 ? components[index - 1]?.componentType : null;
        const nextComponentType = index < lastIndex ? components[index + 1]?.componentType : null;
        const prevIsMargin = prevComponentType === ComponentTypes.Margin;
        // Top-spacing precedence: an explicit Margin component wins (it carries
        // its own classes and zeroes the NEXT block's margin so the two don't
        // stack), then the per-type override, then the generic default below.
        let topMarginClass = index === 0 ? "" : "mt-4 md:mt-6 lg:mt-8";
        if (prevIsMargin) {
          topMarginClass = "mt-0";
        }
        const layoutClass = LAYOUT_CLASSES_BY_TYPE[component.componentType];
        const spacingClass = SPACING_CLASSES_BY_TYPE[component.componentType];
        if (spacingClass && !prevIsMargin && component.componentType !== ComponentTypes.Margin) {
          topMarginClass = spacingClass;
        }
        if (component.componentType === ComponentTypes.Margin) {
          topMarginClass = buildMarginClassName((component as MarginComponent).content);
        }

        // The section hero already owns its trailing rhythm. Let the first
        // editorial block start from that rhythm instead of stacking a second
        // component margin on top of it. The publications search band is the
        // exception: the explanatory text that follows needs a visible white
        // pause after the full-width blue tool.
        if (
          prevComponentType === ComponentTypes.Hero &&
          (component.componentType === ComponentTypes.SectionHeading || component.componentType === ComponentTypes.TextBlock)
        ) {
          // Un titre de section s'ouvre avec la MÊME respiration que les pages de
          // section codées en dur (/statistiques : hero collé + section py-16 = 64px).
          // La marge du hero (40px) FUSIONNE avec celle-ci (margin collapsing) : c'est
          // la plus grande qui gagne, donc 64px donne exactement le même écart — un
          // mt-6 « complémentaire » serait avalé par les 40px du hero. Un TextBlock qui
          // suit directement le hero reste collé au rythme du hero (chapô éditorial).
          topMarginClass =
            component.componentType === ComponentTypes.SectionHeading ? "mt-16" : "mt-0";
        }
        if (prevComponentType === ComponentTypes.SearchBar && component.componentType === ComponentTypes.TextBlock) {
          topMarginClass = "mt-8 md:mt-10";
        }
        // Deux blocs lourds qui se suivent (bandeau d'appel pleine largeur puis
        // panneaux de répertoire) ont besoin d'une vraie pause blanche. Cette marge
        // FUSIONNE avec celle du bloc suivant : il faut donc une valeur supérieure au
        // rythme courant (24px) pour qu'elle l'emporte au lieu d'être avalée.
        if (prevComponentType === ComponentTypes.CtaBanner && component.componentType === ComponentTypes.TextCardGrid) {
          topMarginClass = "mt-12";
        }

        // Dispatch via the component registry (1:1 replacement for the former 60-case switch).
        // A missing entry renders nothing — the registry's exhaustiveness is unit-tested.
        const renderer = COMPONENT_REGISTRY[component.componentType];
        const content: JSX.Element | null = renderer ? renderer(component, effectiveConfig) : null;

        const positionClass =
          (index === 0 ? firstWidgetClassName : "") ||
          (index === lastIndex ? lastWidgetClassName : "") ||
          "";
        // A full-width search band immediately following a section hero is one
        // composed module: neither renderer should inject a white gutter between
        // the two. Other hero/content combinations keep their normal breathing room.
        const adjacentHeroSearchClass =
          component.componentType === ComponentTypes.Hero && nextComponentType === ComponentTypes.SearchBar
            ? "[&>.bcc-section-hero]:!mb-0"
            : component.componentType === ComponentTypes.SearchBar && prevComponentType === ComponentTypes.Hero
              ? "[&>section]:!my-0"
              : "";
        const fluidContainerClass =
          layoutMode === "fluid" && FLUID_CONTAINER_TYPES.has(component.componentType)
            ? FLUID_CONTAINER_CLASS
            : "";
        const wrapperClass = [topMarginClass, layoutClass, positionClass, adjacentHeroSearchClass, fluidContainerClass]
          .filter(Boolean)
          .join(" ");
        return (
          <div key={component.key ?? `cmp-${index}`} className={wrapperClass}>
            {content}
          </div>
        );
      })}
    </div>
  );
}
